import React from 'react';
import { Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import AccessControl from './management/AccessControl';
import CardManager from './management/CardManager';


import ProtectedRoute from './ProtectedRoute';

import Login from './sections/authentication/login/Login';
import Register from './sections/authentication/register/Register';
import NotFound from './pages/Page404';

import DashboardApp from './pages/DashboardApp';
import Products from './products/Products';
import Members from './management/Members';
import Calendar from './calendars/Calendar';
import CourtsCalendar from './calendars/CourtsCalendar';
import Payment from './payments/Payement';
import Settings from './management/Settings';
import Profile from './members/Profile';
import Plans from './plans/Plans';
import Pennants from './pennant/Pennants';
import MatchPointsSummary from './scoring/MatchPointsSummary';
import MemberDashboardApp from './members/MemberDashboardApp';
import SinglePennantLadder from './members/SinglePennantLadder';
import FirestoreExplorer from './management/FirestoreExplorer';
import PrintableScoreSheet from './scoring/PrintableScoreSheet';
import BulkPrintableScoreSheets from './scoring/BulkPrintableScoreSheets';
import Ladder from './pennant/Ladder';
import PendingRequests from './pennant/PendingRequests';
import CourtBookingMemberSide from './members/CourtBookingMemberSide';
import MyBookingsMemberSide from './members/MyBookingsMemberSide';
import ManageUsers from './management/ManageUsers';
import Conversation from './messages/Conversation';
import MemberDirectory from './members/MemberDirectory';
import LandingPage from './members/LandingPage';
import MemberPlansManager from './members/MemberPlansManager';
import MemberPennants from './members/MemberPennants';
import MemberAndPennantInfo from './members/MemberAndPennantInfo';
import PublicMemberAndPennantInfo from './members/PublicMemberAndPennantInfo';
import EmailTemplateEditor from './management/EmailTemplateEditor';
import WebsiteManagement from './website/WebsiteManagement';
import PennantDraws from './pennant/PennantDraws';
import RunPennantDay from './pennant/RunPennant';


export default function routes(user) {

  let rootElement;

  if (!user) {
    rootElement = <LogoOnlyLayout />;

  } else if (user.role === 'member') {
      rootElement = <Navigate to="/dashboard/member-home" replace />;
    }
   else if (user.role === 'staff' || user.role === 'admin') {
    rootElement = <Navigate to="/dashboard/app" replace />;

  } else {
    rootElement = <Navigate to="/login" replace />;
  }

  return [
    {
      path: '/',
      element: rootElement,
      children: [
        { path: '/', element: <Navigate to="/login" /> },
        { path: 'public', element: <LandingPage /> },
        { path: 'member-pennant-info', element: <PublicMemberAndPennantInfo /> },
        { path: 'login', element: <Login /> },
        { path: 'register', element: <Register /> },
        { path: '*', element: <NotFound /> }
      ]
    },

    {
      path: '/dashboard',
      element: (
        <ProtectedRoute allowedRoles={['admin', 'staff', 'member']}>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          path: 'manage-users',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <ManageUsers />
            </ProtectedRoute>
          )
        },
        {
          path: 'posts',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <WebsiteManagement />
            </ProtectedRoute>
          )
        },
        {
          path: 'email-template-editor',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <EmailTemplateEditor />
            </ProtectedRoute>
          )
        },
        {
          path: 'access-control',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <AccessControl />
            </ProtectedRoute>
          )
        },
        {
          path: 'card-manager',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <CardManager />
            </ProtectedRoute>
          )
        },
        {
          path: 'app',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <DashboardApp />
            </ProtectedRoute>
          )
        },
        {
          path: 'members',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Members />
            </ProtectedRoute>
          )
        },
        {
          path: 'plans',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Plans />
            </ProtectedRoute>
          )
        },
        {
          path: 'products',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Products />
            </ProtectedRoute>
          )
        },
        {
          path: 'calendar',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Calendar />
            </ProtectedRoute>
          )
        },
        {
          path: 'pennants',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Pennants />
            </ProtectedRoute>
          )
        },
        {
          path: 'run-pennant',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <RunPennantDay />
            </ProtectedRoute>
          )
        },
        {
          path: 'ladder',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Ladder />
            </ProtectedRoute>
          )
        },
        {
          path: 'draws',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <PennantDraws />
            </ProtectedRoute>
          )
        },
        {
          path: 'results',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <MatchPointsSummary />
            </ProtectedRoute>
          )
        },
        {
          path: 'payment',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Payment />
            </ProtectedRoute>
          )
        },
        {
          path: 'pending-requests',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <PendingRequests />
            </ProtectedRoute>
          )
        },
        {
          path: 'settings',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Settings />
            </ProtectedRoute>
          )
        },
        {
          path: 'courts-calendar',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CourtsCalendar />
              </LocalizationProvider>
            </ProtectedRoute>
          )
        },

        {
          path: 'member-home/*',
          element: (
            <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
              <MemberDashboardApp />
            </ProtectedRoute>
          )
        },

            {
              path: 'overview',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <MemberDashboardApp/>
                </ProtectedRoute>
              )
            },
            {
              path: 'public',
              element: (
                <ProtectedRoute allowedRoles={['member']}>
                  <LandingPage />
                </ProtectedRoute>
              )
            },
            {
              path: 'member-and-pennant-info',
              element: (
                <ProtectedRoute allowedRoles={['member']}>
                  <MemberAndPennantInfo />
                </ProtectedRoute>
              )
            },
            {
              path: 'profile',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <Profile />
                </ProtectedRoute>
              )
            },
            {
              path: 'my-pennants',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <MemberPennants />
                </ProtectedRoute>
              )
            },
            {
              path: 'membership',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <MemberPlansManager />
                </ProtectedRoute>
              )
            },
            {
              path: 'court-bookings',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <CourtBookingMemberSide />
                </ProtectedRoute>
              )
            },
            {
              path: 'my-bookings',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <MyBookingsMemberSide />
                </ProtectedRoute>
              )
            },
            {
              path: 'events',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <Calendar />
                </ProtectedRoute>
              )
            },
            {
              path: 'pennant/:pennantId/ladder',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <SinglePennantLadder />
                </ProtectedRoute>
              )
            },
            {
              path: 'messages',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <Conversation />
                </ProtectedRoute>
              )
            }
            ,
            {
              path: 'member-directory',
              element: (
                <ProtectedRoute allowedRoles={['member', 'admin', 'staff']}>
                  <MemberDirectory />
                </ProtectedRoute>
              )
            }



      ]
    },

    {
      path: '/printable-scoresheet',
      element: (

          <PrintableScoreSheet />

      )
    },
    {
      path: '/bulk-printable-scoresheets',
      element: (

          <BulkPrintableScoreSheets />

      )
    },


    {
      path: '*',
      element: <NotFound />
    }
  ];
}


